import { modelState } from "@/utils/modelState";
import { BaseRateModels } from "@/Rates/BaseRates/BaseRateModel";

export namespace RatesBaseRateOverrides {
    export const BaseRateOverride = Symbol("BaseRateOverride");
    export interface BaseRateOverride {
        [BaseRateOverride]: boolean;
    }

    export interface BaseRateOverrideDto {
        $type: string;
        value: BaseRateOverrideValueDto;
    }

    export interface BaseRateOverrideValueDto {
        //
    }

    export function serializeBaseRateOverride(action: BaseRateOverride): BaseRateOverrideDto {
        if (!action) {
            return null;
        }

        for (let baseRateOverride of baseRateOverrides) {
            if (baseRateOverride.isInstanceOf(action)) {
                return {
                    $type: baseRateOverride.key,
                    value: baseRateOverride.serialize(action)
                };
            }
        }

        throw new Error("Invalid status type");
    }

    export function deserializeBaseRateOverride(dto: BaseRateOverrideDto): BaseRateOverride {
        if (!dto) {
            return null;
        }

        for (let baseRateOverride of baseRateOverrides) {
            if (baseRateOverride.key === dto.$type) {
                return baseRateOverride.deserialize(dto.value);
            }
        }

        throw new Error("Invalid status type");
    }

    export function getBaseRateOverrideName(status: BaseRateOverride): string {

        for (let baseRateOverride of baseRateOverrides) {
            if (baseRateOverride.isInstanceOf(status)) {
                return baseRateOverride.name;
            }
        }

        throw new Error("Invalid status type");
    }

    export function createBaseRateOverride(key: string): BaseRateOverride {

        for (let baseRateOverride of baseRateOverrides) {
            if (baseRateOverride.key === key) {
                return baseRateOverride.create();
            }
        }

        throw new Error("Invalid baseRateOverride key");
    }



    export const ExistingBaseRateOverride = Symbol("ExistingBaseRateOverride");
    export interface ExistingBaseRateOverride extends BaseRateOverride {
        [ExistingBaseRateOverride]: boolean;
        BaseRateId: string;
        BaseRateVersion: number;
    }

    export function isExistingBaseRateOverride(val: BaseRateOverride): val is ExistingBaseRateOverride {
        return val && (val as ExistingBaseRateOverride)[ExistingBaseRateOverride] === true;
    }

    export interface ExistingBaseRateOverrideValueDto extends BaseRateOverrideValueDto {
        baseRateId: string;
        baseRateVersion: number;
    }

    export const ExistingBaseRateOverrideDefinition: BaseRateOverrideDefinitionT<ExistingBaseRateOverride, ExistingBaseRateOverrideValueDto> =
    {
        key: "Existing",
        name: "Existing",
        isInstanceOf: (baseRateOverride: BaseRateOverride) => isExistingBaseRateOverride(baseRateOverride),
        create: (): ExistingBaseRateOverride => {
            return {
                [BaseRateOverride]: true,
                [ExistingBaseRateOverride]: true,
                BaseRateId: null,
                BaseRateVersion: null
            };
        },
        serialize: (baseRateOverride: ExistingBaseRateOverride): ExistingBaseRateOverrideValueDto => {
            return {
                baseRateId: baseRateOverride.BaseRateId,
                baseRateVersion: baseRateOverride.BaseRateVersion
            };
        },
        deserialize: function (this: BaseRateOverrideDefinition, dto: ExistingBaseRateOverrideValueDto) {
            const existing = this.create() as ExistingBaseRateOverride;
            existing.BaseRateId = dto.baseRateId;
            existing.BaseRateVersion = dto.baseRateVersion;
            return existing;
        }
    };


    export const CustomBaseRateOverride = Symbol("CustomBaseRateOverride");
    export interface CustomBaseRateOverride extends BaseRateOverride {
        [CustomBaseRateOverride]: boolean;
        BaseRates: BaseRateModels.BaseRatesModel;
    }

    export function isCustomBaseRateOverride(val: BaseRateOverride): val is CustomBaseRateOverride {
        return val && (val as CustomBaseRateOverride)[CustomBaseRateOverride] === true;
    }

    export interface CustomBaseRateOverrideValueDto extends BaseRateOverrideValueDto {
        baseRates: BaseRateModels.BaseRatesModelDto;
    }

    export const CustomBaseRateOverrideDefinition: BaseRateOverrideDefinition = {
        key: "Custom",
        name: "Custom",
        isInstanceOf: (baseRateOverride: BaseRateOverride) => isCustomBaseRateOverride(baseRateOverride),
        create: (): CustomBaseRateOverride => {
            return {
                [BaseRateOverride]: true,
                [CustomBaseRateOverride]: true,
                BaseRates: {
                    grossAmount: modelState(0),
                    netAmount: modelState(0),
                    serviceChargeAmount: modelState(0),
                    vatAmount: modelState(0)
                }
            };
        },
        serialize: (baseRateOverride: CustomBaseRateOverride): CustomBaseRateOverrideValueDto => {
            return {
                baseRates: BaseRateModels.serializeBaseRates(baseRateOverride.BaseRates)
            };
        },
        deserialize: function (this: BaseRateOverrideDefinition, dto: CustomBaseRateOverrideValueDto) {
            const custom = this.create() as CustomBaseRateOverride;
            custom.BaseRates = BaseRateModels.deserializeBaseRates(dto.baseRates);
            return custom;
        }
    };


    interface BaseRateOverrideDefinition {
        key: string;
        name: string;
        isInstanceOf: (baseRateOverride: BaseRateOverride) => boolean;
        create: () => BaseRateOverride;
        serialize: (baseRateOverride: BaseRateOverride) => BaseRateOverrideValueDto;
        deserialize: (dto: BaseRateOverrideValueDto) => BaseRateOverride;
    }

    interface BaseRateOverrideDefinitionT<T, TDto> extends Omit<BaseRateOverrideDefinition, 'create' | 'serialize' | 'deserialize'> {
        create: () => T;
        serialize: (baseRateOverride: T) => TDto;
        deserialize: (dto: TDto) => T;
    }

    const baseRateOverrides: BaseRateOverrideDefinition[] = [
        ExistingBaseRateOverrideDefinition,
        CustomBaseRateOverrideDefinition
    ]
}
